import { AREA_TAGS, DRAFTS_FILTERS, flowLane, HIT_ROLES, KINDS, LANES, lanesFor, projectCode, type BoardItem, type Lane, type Viewer } from "@boem/core/shared";
import type { ReactNode } from "react";
import { DUE_FILTERS } from "@boem/core/due";
import type { Filters, NotesFilter, Sort, TeamsFilter } from "../filters.ts";
import { GROUP_BY, GROUP_LABEL, projectChipLabel as projectChipText, type GroupBy } from "../grouping.ts";
import { Avatar } from "../util.tsx";
import { Icon } from "./Icons.tsx";

// The board's header (C1, docs/design): one line with the board name, count chips and
// the viewer's buttons, the ribbon under it, then the one-row toolbar whose "Filters"
// button opens everything that isn't a queue chip, the search or the sort.

/**
 * Notes on every card (docs/design/actions.md, A refined): one icon toggle in the toolbar. Pressed while
 * the notes show (the default, drawn quietly); marked while they are hidden. A dot says some cards were
 * flipped by hand since; pressing it again sets every card to the new state.
 */
export function NotesToggle({ all, flipped, onToggle }: { all: boolean; flipped: number; onToggle: () => void }) {
  const tip = `${all ? "Hide" : "Show"} notes on every card${flipped ? ` (${flipped} card${flipped === 1 ? "" : "s"} set by hand)` : ""}`;
  return (
    <button type="button" className="chip nt-all tip" aria-pressed={all} aria-label="Notes on every card" data-tip={tip} data-mixed={flipped ? "1" : undefined} onClick={onToggle}>
      <Icon name={all ? "notes" : "notes-off"} />
    </button>
  );
}

/** One count chip on the header line: "61 to do". `lane` colours the number. */
export interface CountChip {
  n: number | string;
  label: string;
  lane?: "todo" | "review" | "deploy" | "verify" | "high" | "closed" | "done";
}

export function CountChips({ chips, title }: { chips: CountChip[]; title?: string }) {
  return (
    <span className="vb-counts" title={title}>
      {chips.map((c) => (
        <span key={c.label} className="cnt" data-lane={c.lane}>
          <b>{c.n}</b> {c.label}
        </span>
      ))}
    </span>
  );
}

/** The chips for a board or a project board: the queues (PR review on OEMUAP: `review` given), high priority still open, closed. */
export function boardChips(c: { todo: number; review?: number; deploy: number; verify: number; high: number; closed: number }, done?: { done: number; total: number } | null): CountChip[] {
  return [
    { n: c.todo, label: "to do", lane: "todo" },
    ...(c.review != null ? [{ n: c.review, label: "PR review", lane: "review" as const }] : []),
    { n: c.deploy, label: "deploy", lane: "deploy" },
    { n: c.verify, label: "verify", lane: "verify" },
    { n: c.high, label: "high", lane: "high" },
    { n: c.closed, label: "closed", lane: "closed" },
    ...(done && done.total ? [{ n: `${done.done}/${done.total}`, label: "done", lane: "done" as const }] : []),
  ];
}

/** What a board is called on the header line: "Board", "Archive", or "02 · Returns and refunds". */
export function boardName(project: Pick<BoardItem, "num" | "title" | "projectNo"> | null, archive = false): string {
  if (archive) return "Archive";
  if (!project) return "Board";
  return `${project.projectNo != null ? projectCode(project.projectNo) : `#${project.num}`} · ${project.title}`;
}

/** Every item on the board as one thin segment in its queue's colour; click one to go to it. */
export function Ribbon({ items, label, onJump }: { items: BoardItem[]; label: (it: BoardItem) => string; onJump: (num: number) => void }) {
  return (
    <div className="ribbon rb8" role="group" aria-label="Every item on this board in order, one segment each: click one to go to it">
      {items.map((it) => {
        const lane = LANES.find((l) => l.id === flowLane(it))!;
        const t = `${label(it)} · ${lane.label} · ${it.title}`;
        return (
          <button
            key={it.num}
            type="button"
            className={`rib s-${it.status} l-${lane.id}`}
            data-hi={it.priority === "high" ? "1" : undefined}
            title={t}
            aria-label={t}
            onClick={() => onJump(it.num)}
          />
        );
      })}
    </div>
  );
}

/** The viewer's avatar; its menu holds who they are, Team (admins), Request a feature and Sign out. */
export function AvatarMenu({
  viewer,
  open,
  onToggle,
  onTeam,
  onPlatform,
  onRequest,
  onSignOut,
}: {
  viewer: Viewer;
  open: boolean;
  onToggle: () => void;
  onTeam?: () => void;
  /** a platform admin's list of boards that need an owner (orgs.md 1.4) */
  onPlatform?: () => void;
  onRequest?: () => void;
  onSignOut: () => void;
}) {
  // on a personal board: owner, editor or viewer there (the company role gives nothing there)
  const role = viewer.org?.kind === "personal" ? (viewer.org.role ?? "viewer") : viewer.isAdmin ? "admin" : viewer.role;
  return (
    <span className="rel me-menu" onClick={(e) => e.stopPropagation()}>
      <button type="button" className="av-btn" aria-haspopup="menu" aria-expanded={open} title={`${viewer.name} · ${role}`} onClick={onToggle}>
        <Avatar name={viewer.name} />
        <span className="n">{role}</span>
      </button>
      {open && (
        <div className="menu" role="menu" aria-label={`${viewer.name}'s menu`}>
          <div className="mh">
            <b>{viewer.name}</b> &middot; {role}
            {viewer.email && <span className="mh-mail">{viewer.email}</span>}
          </div>
          {onTeam && (
            <button type="button" role="menuitem" title="Add people, change roles and flags, deactivate" onClick={onTeam}>
              Team
            </button>
          )}
          {onPlatform && (
            <button type="button" role="menuitem" title="Personal boards whose owner left the company, and closed boards" onClick={onPlatform}>
              Boards that need an owner
            </button>
          )}
          {onRequest && (
            <button type="button" role="menuitem" onClick={onRequest}>
              Request a feature
            </button>
          )}
          <button type="button" role="menuitem" onClick={onSignOut}>
            Sign out
          </button>
        </div>
      )}
    </span>
  );
}

/** "Filters · n": the one button for every filter that isn't a queue chip or the search. */
export function FiltersMenu({ open, count, onToggle, children }: { open: boolean; count: number; onToggle: () => void; children: ReactNode }) {
  return (
    <span className="rel fmenu" onClick={(e) => e.stopPropagation()}>
      <button
        type="button"
        className="chip filters-btn"
        aria-haspopup="dialog"
        aria-expanded={open}
        data-on={count ? "1" : undefined}
        title={count ? `${count} filter${count === 1 ? "" : "s"} on` : "Where, who hits it, assigned to, Teams, notes, kind and more"}
        onClick={onToggle}
      >
        Filters <span className="n">&middot; {count}</span>
      </button>
      {open && (
        <div className="fpop" role="dialog" aria-label="Filters">
          {children}
        </div>
      )}
    </span>
  );
}

/** The queue chips' short names, for the compact row in a panel. */
export const LANE_SHORT: Record<Lane, string> = { todo: "To do", review: "PR review", deploy: "Deploy", verify: "Verify", closed: "Closed" };

/**
 * A project panel's filter row (docs/design 4.3): one sticky line with a small search, the queue
 * chips with short names and counts, the assignee button (a stack of avatars), and "Filters · n" for
 * Kind, High and Not reviewed. "Open as board" carries all of it into the board's full toolbar.
 */
export function PanelFilterRow({
  q,
  onQ,
  f,
  setF,
  lanes,
  counts,
  assignee,
  open,
  onToggle,
  company = true,
}: {
  q: string;
  onQ: (q: string) => void;
  f: Filters;
  setF: (fn: (cur: Filters) => Filters) => void;
  /** items in each queue */
  lanes: Record<Lane, number>;
  /** OEMUAP: the PR review queue too (a personal board has no GitHub) */
  company?: boolean;
  counts: Pick<FilterCounts, "kind" | "notReviewed">;
  /** the assignee button, compact */
  assignee?: ReactNode;
  open: boolean;
  onToggle: () => void;
}) {
  const n = (f.kind ? 1 : 0) + (f.asg.length ? 1 : 0) + (f.high ? 1 : 0) + (f.notReviewed ? 1 : 0);
  const row = (label: string, body: ReactNode) => (
    <div className="fp-row" key={label}>
      <div className="fl">{label}</div>
      <div className="asg-chips">{body}</div>
    </div>
  );
  return (
    <div className="pf-row" role="search" aria-label="Filter this project">
      <input className="search" type="search" value={q} placeholder="Search here…" aria-label="Search this project" onChange={(e) => onQ(e.target.value)} />
      <span className="pf-lanes" role="group" aria-label="Queues">
        {lanesFor(company).map((l) => (
          <button
            key={l.id}
            type="button"
            className="chip sm lane-chip"
            data-lane={l.id}
            title={`${l.label}: ${l.hint}`}
            aria-pressed={f.lanes.has(l.id)}
            onClick={() =>
              setF((cur) => {
                const next = new Set(cur.lanes);
                if (next.has(l.id)) next.delete(l.id);
                else next.add(l.id);
                return { ...cur, lanes: next };
              })
            }
          >
            {LANE_SHORT[l.id]} <span className="n">{lanes[l.id]}</span>
          </button>
        ))}
      </span>
      {assignee}
      <FiltersMenu open={open} count={n} onToggle={onToggle}>
        {row(
          "Kind",
          <>
            {KINDS.filter((k) => k.id !== "project").map((k) => {
              const id = k.id as Exclude<typeof k.id, "project">;
              return (
                <button key={k.id} type="button" className="chip sm" aria-pressed={f.kind === id} title={k.hint} onClick={() => setF((cur) => ({ ...cur, kind: cur.kind === id ? null : id }))}>
                  {k.label} <span className="n">{counts.kind[k.id] ?? 0}</span>
                </button>
              );
            })}
            <button type="button" className="chip sm" aria-pressed={f.kind === "none"} onClick={() => setF((cur) => ({ ...cur, kind: cur.kind === "none" ? null : "none" }))}>
              Not set <span className="n">{counts.kind.none ?? 0}</span>
            </button>
          </>,
        )}
        {row(
          "More",
          <>
            <button type="button" className="chip sm" aria-pressed={f.high} onClick={() => setF((cur) => ({ ...cur, high: !cur.high }))}>
              High
            </button>
            <button type="button" className="chip sm" aria-pressed={f.notReviewed} title="Nobody has reacted, noted, assigned or changed it yet" onClick={() => setF((cur) => ({ ...cur, notReviewed: !cur.notReviewed }))}>
              Not yet reviewed <span className="n">{counts.notReviewed}</span>
            </button>
          </>,
        )}
        {n > 0 && (
          <div className="fp-foot">
            <button type="button" className="chip sm" onClick={() => setF((cur) => ({ ...cur, kind: null, asg: [], high: false, notReviewed: false }))}>
              Clear these filters
            </button>
          </div>
        )}
      </FiltersMenu>
    </div>
  );
}

export const TEAMS_LABEL: Record<TeamsFilter, string> ={ in: "Added via Teams", resolved: "Resolved via Teams", any: "Either" };
export const NOTES_LABEL: Record<NotesFilter, string> = { all: "Everyone", people: "People only", ai: "AI only" };
export const SORT_LABEL: Record<Sort, string> = { num: "Item no.", priority: "High first", newest: "Newest", attention: "Most discussed", due: "Due date" };
/** The Due filter's chip in the active-filter row: "Overdue", "Due this week", ... */
export const DUE_LABEL = Object.fromEntries(DUE_FILTERS.map((d) => [d.id, d.label])) as Record<(typeof DUE_FILTERS)[number]["id"], string>;

/** What the Filters popover counts beside each choice (open items for people and roles). */
export interface FilterCounts {
  tag: Record<string, number>;
  role: Record<string, number>;
  asg: Record<string, number>;
  unassigned: number;
  tmIn: number;
  tmRes: number;
  tmAny: number;
  ai: number;
  kind: Record<string, number>;
  nondevOpen: number;
  notReviewed: number;
  /** items in no project (OEM-260), for the Project row's No project */
  noProject?: number;
  /** drafts in this view's scope, everyone's (OEM-286) */
  drafts?: number;
  /** items in each Due bucket (OEM-267): overdue (open), due this week, due later, no date */
  due?: Record<string, number>;
}

/** The inside of the Filters popover: each filter as a labelled row of choices. */
export function FilterPanel({
  f,
  setF,
  counts,
  projects,
  archive = false,
  company = true,
  onCopyReport,
  onClear,
  group,
  onGroup,
  count,
  projectView = true,
}: {
  f: Filters;
  setF: (fn: (cur: Filters) => Filters) => void;
  counts: FilterCounts;
  /** the Project filter's choices; null where the board is a project's own */
  projects: Pick<BoardItem, "num" | "title" | "projectNo">[] | null;
  archive?: boolean;
  /** OEMUAP: Teams and Non-dev are company things, left out on a personal board */
  company?: boolean;
  onCopyReport: () => void;
  onClear?: () => void;
  /** the main board's Group by (its select leaves the toolbar under 1024px, like the sort) */
  group?: GroupBy;
  onGroup?: (g: GroupBy) => void;
  /** "70 of 177": the toolbar's count, which sits here on a phone */
  count?: string;
  /** No project and Hide (OEM-260): the main board's and the archive's, not a panel's */
  projectView?: boolean;
}) {
  const one = (key: "tag" | "role" | "teams", v: string) => setF((cur) => ({ ...cur, [key]: cur[key] === v ? null : v }));
  const all = (on: boolean, pick: () => void, label = "All") => (
    <button type="button" className="chip sm fdd-all" aria-pressed={on} onClick={pick}>
      {label}
    </button>
  );
  const row = (label: string, body: ReactNode) => (
    <div className="fp-row" key={label}>
      <div className="fl">{label}</div>
      <div className="asg-chips">{body}</div>
    </div>
  );
  return (
    <>
      {row(
        "Where",
        <>
          {all(!f.tag, () => setF((cur) => ({ ...cur, tag: null })))}
          {AREA_TAGS.map((t) => (
            <button key={t.id} type="button" className="chip sm" aria-pressed={f.tag === t.id} onClick={() => one("tag", t.id)}>
              {t.label} <span className="n">{counts.tag[t.id] ?? 0}</span>
            </button>
          ))}
        </>,
      )}
      {row(
        "Who hits it",
        <>
          {all(!f.role, () => setF((cur) => ({ ...cur, role: null })))}
          {HIT_ROLES.map((r) => (
            <button key={r.id} type="button" className="chip sm" aria-pressed={f.role === r.id} onClick={() => one("role", r.id)}>
              {r.label} <span className="n">{counts.role[r.id] ?? 0}</span>
            </button>
          ))}
        </>,
      )}
      {company && row(
        "Teams",
        <>
          {all(!f.teams, () => setF((cur) => ({ ...cur, teams: null })))}
          {(
            [
              ["in", counts.tmIn],
              ["resolved", counts.tmRes],
              ["any", counts.tmAny],
            ] as const
          ).map(([k, n]) => (
            <button key={k} type="button" className="chip sm" aria-pressed={f.teams === k} onClick={() => one("teams", k)}>
              {TEAMS_LABEL[k]} <span className="n">{n || ""}</span>
            </button>
          ))}
        </>,
      )}
      {row(
        "Notes",
        (["all", "people", "ai"] as const).map((k) => (
          <button
            key={k}
            type="button"
            className={`chip sm${k === "all" ? " fdd-all" : ""}`}
            aria-pressed={f.notes === k}
            title={k === "people" ? "Hide notes written by someone's AI app" : k === "ai" ? "Only items with notes from an AI app, and only those notes" : "Every note"}
            onClick={() => setF((cur) => ({ ...cur, notes: k }))}
          >
            {NOTES_LABEL[k]} {k === "ai" && <span className="n">{counts.ai || ""}</span>}
          </button>
        )),
      )}
      {row(
        "Kind",
        <>
          {all(!f.kind, () => setF((cur) => ({ ...cur, kind: null })))}
          {KINDS.filter((k) => k.id !== "project").map((k) => {
            const id = k.id as Exclude<typeof k.id, "project">;
            return (
              <button key={k.id} type="button" className="chip sm" aria-pressed={f.kind === id} title={k.hint} onClick={() => setF((cur) => ({ ...cur, kind: cur.kind === id ? null : id }))}>
                {k.label} <span className="n">{counts.kind[k.id] ?? 0}</span>
              </button>
            );
          })}
          <button type="button" className="chip sm" aria-pressed={f.kind === "none"} onClick={() => setF((cur) => ({ ...cur, kind: cur.kind === "none" ? null : "none" }))}>
            Not set <span className="n">{counts.kind.none ?? 0}</span>
          </button>
        </>,
      )}
      {row(
        "Due",
        <>
          {all(!f.due, () => setF((cur) => ({ ...cur, due: null })))}
          {DUE_FILTERS.map((d) => (
            <button key={d.id} type="button" className="chip sm" aria-pressed={f.due === d.id} title={d.hint} onClick={() => setF((cur) => ({ ...cur, due: cur.due === d.id ? null : d.id }))}>
              {d.label} <span className={`n${d.id === "overdue" && counts.due?.overdue ? " fcount-overdue" : ""}`}>{counts.due?.[d.id] ?? 0}</span>
            </button>
          ))}
        </>,
      )}
      {projects &&
        projects.length > 0 &&
        row(
          "Project",
          <>
            {all(f.project == null && !f.noProject, () => setF((cur) => ({ ...cur, project: null, noProject: false })), archive ? "Main board" : "All")}
            {/* OEM-260: only the items in no project ("just the raw ones") */}
            {projectView && (
              <button
                type="button"
                className="chip sm"
                aria-pressed={f.noProject}
                title="Only items that aren't in any project"
                onClick={() => setF((cur) => ({ ...cur, noProject: !cur.noProject, project: null }))}
              >
                No project {counts.noProject != null && <span className="n">{counts.noProject}</span>}
              </button>
            )}
            {projects.map((p) => (
              <button
                key={p.num}
                type="button"
                className="chip sm"
                aria-pressed={f.project === p.num}
                onClick={() => setF((cur) => ({ ...cur, project: cur.project === p.num ? null : p.num, noProject: false, hideProjects: cur.hideProjects.filter((n) => n !== p.num) }))}
              >
                {projectChipText(p)}
              </button>
            ))}
          </>,
        )}
      {/* OEM-260: leave chosen projects out (everything except 03), several at once; a row of its own, so
          picking one project (above) and hiding some (here) never share a chip */}
      {projectView &&
        projects &&
        projects.length > 0 &&
        row(
          "Hide",
          <>
            {all(!f.hideProjects.length, () => setF((cur) => ({ ...cur, hideProjects: [] })), "None")}
            {projects.map((p) => {
              const on = f.hideProjects.includes(p.num);
              return (
                <button
                  key={p.num}
                  type="button"
                  className="chip sm chip-hide"
                  aria-pressed={on}
                  title={on ? `Show ${projectChipText(p)} again` : `Leave out ${projectChipText(p)}'s items`}
                  onClick={() =>
                    setF((cur) => ({
                      ...cur,
                      hideProjects: on ? cur.hideProjects.filter((n) => n !== p.num) : [...cur.hideProjects, p.num].sort((a, b) => a - b),
                      project: cur.project === p.num ? null : cur.project,
                    }))
                  }
                >
                  {projectChipText(p)}
                </button>
              );
            })}
          </>,
        )}
      {/* drafts (OEM-286): others' are hidden until they're ready; Show drafts shows everyone's, marked Draft */}
      {row(
        "Drafts",
        DRAFTS_FILTERS.map((d) => (
          <button
            key={d.id}
            type="button"
            className={`chip sm${d.id === "hide" ? " fdd-all" : ""}`}
            aria-pressed={(f.drafts ?? "hide") === d.id}
            title={d.hint}
            onClick={() => setF((cur) => ({ ...cur, drafts: d.id }))}
          >
            {d.label} {d.id !== "hide" && counts.drafts != null && <span className="n">{counts.drafts}</span>}
          </button>
        )),
      )}
      {row(
        "More",
        <>
          <button type="button" className="chip sm" aria-pressed={f.high} onClick={() => setF((cur) => ({ ...cur, high: !cur.high }))}>
            High
          </button>
          <button type="button" className="chip sm" aria-pressed={f.notReviewed} title="Nobody has reacted, noted, assigned or changed it yet" onClick={() => setF((cur) => ({ ...cur, notReviewed: !cur.notReviewed }))}>
            Not yet reviewed <span className="n">{counts.notReviewed}</span>
          </button>
          {company && (
            <button
              type="button"
              className="chip sm chip-nondev"
              aria-pressed={f.nondev}
              title="Items kept on the board but not for the developers"
              onClick={() => setF((cur) => ({ ...cur, nondev: !cur.nondev }))}
            >
              Non-dev <span className="n">{counts.nondevOpen || ""}</span>
            </button>
          )}
        </>,
      )}
      {/* under 1024px the sort select leaves the toolbar row (app.css), so it is here instead */}
      <div className="fp-row fp-sort">
        <div className="fl">Sort</div>
        <div className="asg-chips">
          {(Object.keys(SORT_LABEL) as Sort[]).map((s) => (
            <button key={s} type="button" className="chip sm" aria-pressed={f.sort === s} onClick={() => setF((cur) => ({ ...cur, sort: s }))}>
              {SORT_LABEL[s]}
            </button>
          ))}
        </div>
      </div>
      {group && onGroup && (
        <div className="fp-row fp-group">
          <div className="fl">Group by</div>
          <div className="asg-chips">
            {GROUP_BY.map((g) => (
              <button key={g} type="button" className="chip sm" aria-pressed={group === g} onClick={() => onGroup(g)}>
                {GROUP_LABEL[g]}
              </button>
            ))}
          </div>
        </div>
      )}
      <div className="fp-foot">
        {count && (
          <span className="fp-count">
            Showing {count}
          </span>
        )}
        {onClear && (
          <button type="button" className="chip sm" onClick={onClear}>
            Clear these filters
          </button>
        )}
        <button type="button" className="chip sm" title="Copy the items in this view as a list to paste to a developer" onClick={onCopyReport}>
          Copy report
        </button>
      </div>
    </>
  );
}
